import { DestroyRef, inject, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter, finalize, Observable, of, shareReplay, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { User } from '@core/types/User';
import { AccountService } from './account/account.service';
import { AppAccessService } from './app-access.service';
import { AuthScriptLoaderService } from './auth-script-loader.service';

@Injectable({
  providedIn: 'root',
})
// Координирует старт анонимной или пользовательской сессии и переключение между public и private режимами.
export class AppSessionBootstrapService {
  private readonly accountService = inject(AccountService);
  private readonly appAccessService = inject(AppAccessService);
  private readonly authScriptLoaderService = inject(AuthScriptLoaderService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private bootstrapRequest$: Observable<User | null> | null = null;
  private navigationWatcherInitialized = false;

  // Запускает корректный bootstrap приложения в зависимости от начального режима доступа.
  init$(): Observable<User | null> {
    const initialMode = this.appAccessService.getInitialAccessMode();

    this.appAccessService.setAccessMode(initialMode);
    this.initNavigationWatcher();

    if (initialMode === 'public') {
      this.accountService.initPublicSession();

      return of(null);
    }

    return this.ensurePrivateSessionBootstrap$();
  }

  // Лениво поднимает обычную пользовательскую сессию и переиспользует один активный bootstrap-запрос.
  ensurePrivateSessionBootstrap$(): Observable<User | null> {
    if (this.bootstrapRequest$) {
      return this.bootstrapRequest$;
    }

    this.authScriptLoaderService.ensureLoaded();
    this.bootstrapRequest$ = this.accountService.init$().pipe(
      finalize(() => {
        this.bootstrapRequest$ = null;
      }),
      shareReplay(1),
    );

    return this.bootstrapRequest$;
  }

  // Следит за переходом с public route на private route и в этот момент запускает auth bootstrap.
  private initNavigationWatcher(): void {
    if (this.navigationWatcherInitialized) {
      return;
    }

    this.navigationWatcherInitialized = true;
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationStart) => {
        const currentMode = this.appAccessService.$accessMode();
        const nextMode = this.appAccessService.getAccessModeByUrl(event.url);

        this.appAccessService.setAccessMode(nextMode);

        if (currentMode === 'public' && nextMode === 'private') {
          this.ensurePrivateSessionBootstrap$().pipe(take(1)).subscribe();
        }
      });
  }
}
